import React, { useState, useEffect } from 'react';
import { Users, Search, Tag, Filter, Plus, Mail, ShoppingBag, ArrowUpRight, DollarSign, Award } from 'lucide-react';
import type { Customer, CustomerSegment } from '@zarin/types';
import { apiFetch } from '../lib/auth';

interface DisplayCustomer {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  ordersCount: number;
  totalSpentMinor: number;
  tags: string[];
  acceptsMarketing: boolean;
  defaultCity?: string;
  createdAt: string;
}

const INITIAL_CUSTOMERS: DisplayCustomer[] = [];

const PRESET_SEGMENTS = [
  { id: 'all', name: 'All Customers', filter: (_c: DisplayCustomer) => true },
  { id: 'vip', name: 'VIP Spenders (> ₹25,000)', filter: (c: DisplayCustomer) => c.totalSpentMinor >= 2500000 },
  { id: 'repeat', name: 'Repeat Buyers (≥ 2 orders)', filter: (c: DisplayCustomer) => c.ordersCount >= 2 },
  { id: 'marketing', name: 'Newsletter Opted-In', filter: (c: DisplayCustomer) => c.acceptsMarketing },
];

export default function CustomersPage() {
  const [customers, setCustomers] = useState<DisplayCustomer[]>(INITIAL_CUSTOMERS);
  const [loading, setLoading] = useState(true);
  const [activeSegment, setActiveSegment] = useState('all');
  const [search, setSearch] = useState('');
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [newSegmentName, setNewSegmentName] = useState('');

  useEffect(() => {
    async function loadCustomers() {
      try {
        setLoading(true);
        const res = await apiFetch<{ data: any[] }>('/customers');
        if (Array.isArray(res.data)) {
          setCustomers(res.data.map((c: any) => ({
            id: c.id,
            email: c.email,
            firstName: c.first_name || c.firstName,
            lastName: c.last_name || c.lastName,
            phone: c.phone,
            ordersCount: c.orders_count || c.ordersCount || 0,
            totalSpentMinor: c.total_spent_minor || c.totalSpentMinor || 0,
            tags: Array.isArray(c.tags) ? c.tags : (c.tags ? JSON.parse(c.tags) : []),
            acceptsMarketing: Boolean(c.accepts_marketing || c.acceptsMarketing),
            defaultCity: c.default_city || c.defaultCity,
            createdAt: c.created_at || c.createdAt || new Date().toISOString(),
          })));
        }
      } catch (err) {
        console.error('Failed to load customers:', err);
      } finally {
        setLoading(false);
      }
    }
    loadCustomers();
  }, []);

  const currentSegmentFilter = PRESET_SEGMENTS.find(s => s.id === activeSegment)?.filter || ((_c: DisplayCustomer) => true);

  const filteredCustomers = customers.filter(c => {
    const matchesSegment = currentSegmentFilter(c);
    const q = search.toLowerCase();
    const fullName = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
    const matchesSearch =
      fullName.includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.phone && c.phone.includes(q)) ||
      c.tags.some(t => t.toLowerCase().includes(q));
    return matchesSegment && matchesSearch;
  });

  const totalRevenueMinor = customers.reduce((acc, c) => acc + c.totalSpentMinor, 0);
  const avgOrderValueMinor = customers.length > 0 ? Math.round(totalRevenueMinor / customers.reduce((acc, c) => acc + c.ordersCount, 0)) : 0;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Customers & Segments</h1>
          <p style={styles.subtitle}>Customer profiles, lifetime spend telemetry, and dynamic cohort segmentation</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => setShowSegmentModal(true)}>
          <Plus size={16} />
          <span>Create Segment</span>
        </button>
      </div>

      {/* Metrics Row */}
      <div style={styles.metricsRow}>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>
            <Users size={14} color="#C9A84C" /> Total Customers
          </div>
          <div style={styles.metricValue}>{customers.length}</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>
            <DollarSign size={14} color="#10B981" /> Total Customer LTV
          </div>
          <div style={styles.metricValue}>₹{(totalRevenueMinor / 100).toLocaleString('en-IN')}</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>
            <Award size={14} color="#C9A84C" /> VIP Ratio (&gt; ₹25k)
          </div>
          <div style={styles.metricValue}>
            {Math.round((customers.filter(c => c.totalSpentMinor >= 2500000).length / customers.length) * 100)}%
          </div>
        </div>
      </div>

      {/* Segment Tabs */}
      <div style={styles.tabsRow}>
        {PRESET_SEGMENTS.map(seg => (
          <button
            key={seg.id}
            onClick={() => setActiveSegment(seg.id)}
            style={{
              ...styles.tabBtn,
              ...(activeSegment === seg.id ? styles.tabBtnActive : {}),
            }}
          >
            {seg.name} ({customers.filter(seg.filter).length})
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={16} color="#888" />
          <input
            type="text"
            placeholder="Search by customer name, email, phone, or tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>

      {/* Customers Table */}
      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>City</th>
              <th style={styles.th}>Orders</th>
              <th style={styles.th}>Lifetime Spend</th>
              <th style={styles.th}>Tags</th>
              <th style={styles.th}>Marketing</th>
              <th style={styles.thRight}>Customer Since</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '48px 24px', textAlign: 'center', color: '#8A8A8E' }}>
                  {loading ? 'Loading customer accounts…' : 'No customer records found. As customers place orders, profiles will appear here.'}
                </td>
              </tr>
            ) : (
              filteredCustomers.map((cust) => (
                <tr key={cust.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div>
                      <div style={{ color: '#FFF', fontWeight: 600 }}>
                        {cust.firstName ? `${cust.firstName} ${cust.lastName || ''}` : 'Guest Customer'}
                      </div>
                      <div style={{ fontSize: 12, color: '#8A8A8E' }}>{cust.email}</div>
                      {cust.phone && <div style={{ fontSize: 11, color: '#666' }}>{cust.phone}</div>}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ color: '#DDD' }}>{cust.defaultCity || '—'}</span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ShoppingBag size={13} color="#C9A84C" />
                      <span style={{ fontWeight: 600, color: '#FFF' }}>{cust.ordersCount}</span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontWeight: 600, color: '#10B981' }}>
                      ₹{(cust.totalSpentMinor / 100).toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {cust.tags.map((t, idx) => (
                        <span key={idx} style={styles.tagBadge(t.includes('VIP'))}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.marketingBadge(cust.acceptsMarketing)}>
                      {cust.acceptsMarketing ? 'Subscribed' : 'No Consent'}
                    </span>
                  </td>
                  <td style={styles.tdRight}>
                    <span style={{ fontSize: 12, color: '#888' }}>
                      {new Date(cust.createdAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Segment Builder Modal */}
      {showSegmentModal && (
        <div style={styles.modalOverlay} onClick={() => setShowSegmentModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Create Segment Definition</h3>
            <p style={styles.modalSubtitle}>Group customers based on behavioral rules, spend thresholds, and tags</p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                setShowSegmentModal(false);
              }}
              style={styles.form}
            >
              <div style={styles.field}>
                <label style={styles.label}>Segment Name</label>
                <input
                  type="text"
                  value={newSegmentName}
                  onChange={(e) => setNewSegmentName(e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Inactive High-Rollers"
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Filter Criteria</label>
                <div style={styles.ruleBox}>
                  <select style={styles.selectInput}>
                    <option value="total_spent_minor">Total Spent (Minor)</option>
                    <option value="orders_count">Orders Count</option>
                    <option value="tags">Customer Tag</option>
                  </select>
                  <select style={styles.selectInput}>
                    <option value="gte">&gt;= Greater Than Or Equal</option>
                    <option value="lte">&lt;= Less Than Or Equal</option>
                    <option value="contains">Contains</option>
                  </select>
                  <input style={styles.ruleInput} placeholder="Value (e.g. 50000)" />
                </div>
              </div>

              <div style={styles.modalActions}>
                <button type="button" onClick={() => setShowSegmentModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitBtn}>
                  Save Segment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, any> = {
  page: {
    padding: '28px 32px',
    maxWidth: 1200,
    margin: '0 auto',
    color: '#E5E5E5',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20,
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 26,
    fontWeight: 600,
    color: '#FFFFFF',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#8A8A8E',
    marginTop: 6,
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#C9A84C',
    color: '#0A0A0A',
    border: 'none',
    padding: '9px 16px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  metricsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  metricCard: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    padding: '16px 20px',
  },
  metricLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: '#8A8A8E',
    marginBottom: 8,
  },
  metricValue: {
    fontSize: 22,
    fontWeight: 600,
    color: '#FFF',
  },
  tabsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
    borderBottom: '1px solid #262626',
    paddingBottom: 8,
  },
  tabBtn: {
    background: 'transparent',
    border: 'none',
    padding: '6px 14px',
    borderRadius: 4,
    color: '#8A8A8E',
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 500,
  },
  tabBtnActive: {
    background: '#1F1F1F',
    color: '#C9A84C',
    fontWeight: 600,
  },
  toolbar: {
    marginBottom: 16,
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 6,
    padding: '8px 14px',
    maxWidth: 440,
  },
  searchInput: {
    background: 'transparent',
    border: 'none',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
    width: '100%',
  },
  tableCard: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    overflow: 'hidden',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
  },
  th: {
    padding: '12px 18px',
    background: '#1A1A1A',
    color: '#8A8A8E',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #262626',
  },
  thRight: {
    padding: '12px 18px',
    background: '#1A1A1A',
    color: '#8A8A8E',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #262626',
    textAlign: 'right',
  },
  tr: {
    borderBottom: '1px solid #1F1F1F',
  },
  td: {
    padding: '14px 18px',
    fontSize: 13,
    color: '#D4D4D8',
    verticalAlign: 'middle',
  },
  tdRight: {
    padding: '14px 18px',
    textAlign: 'right',
    verticalAlign: 'middle',
  },
  tagBadge: (isVip: boolean) => ({
    background: isVip ? 'rgba(201, 168, 76, 0.15)' : '#222',
    color: isVip ? '#C9A84C' : '#AAA',
    border: isVip ? '1px solid rgba(201, 168, 76, 0.3)' : '1px solid #333',
    padding: '2px 7px',
    borderRadius: 4,
    fontSize: 11,
  }),
  marketingBadge: (consented: boolean) => ({
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 4,
    background: consented ? 'rgba(16, 185, 129, 0.15)' : 'rgba(113, 113, 122, 0.15)',
    color: consented ? '#10B981' : '#A1A1AA',
  }),
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#141414',
    border: '1px solid #2A2A2A',
    borderRadius: 10,
    width: 500,
    maxWidth: '90vw',
    padding: 24,
  },
  modalTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 20,
    fontWeight: 600,
    color: '#FFF',
    margin: 0,
  },
  modalSubtitle: {
    fontSize: 12,
    color: '#8A8A8E',
    marginTop: 4,
    marginBottom: 20,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: '#A1A1AA',
  },
  input: {
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
  },
  ruleBox: {
    display: 'flex',
    gap: 8,
  },
  selectInput: {
    flex: 1,
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px',
    color: '#FFF',
    fontSize: 12,
  },
  ruleInput: {
    flex: 1,
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px',
    color: '#FFF',
    fontSize: 12,
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 10,
  },
  cancelBtn: {
    background: '#27272A',
    border: 'none',
    borderRadius: 6,
    padding: '8px 16px',
    color: '#CCC',
    fontSize: 13,
    cursor: 'pointer',
  },
  submitBtn: {
    background: '#C9A84C',
    border: 'none',
    borderRadius: 6,
    padding: '8px 18px',
    color: '#0A0A0A',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
};
